import { useMemo, useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '../ui'
import { useToast } from '../../hooks/useToast'
import { submitSupportReport, uploadSupportScreenshot } from '../../lib/supportApi'
import { useAuthStore } from '../../stores/auth'

const MAX_MESSAGE_LENGTH = 4000

export function SupportReportWidget() {
  const location = useLocation()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const currentShopId = useAuthStore((s) => s.currentShopId)

  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageError, setMessageError] = useState('')
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const route = `${location.pathname}${location.search}`

  const clientMetadata = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userLocalTime: new Date().toISOString(),
    }),
    [location.hash, location.pathname, location.search]
  )

  const resetForm = () => {
    setMessage('')
    setMessageError('')
    setScreenshotFile(null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmedMessage = message.trim()

    if (!trimmedMessage) {
      setMessageError('Please tell us what went wrong.')
      return
    }

    setMessageError('')
    setIsSubmitting(true)

    try {
      let screenshotUrl: string | null = null

      if (screenshotFile) {
        try {
          screenshotUrl = await uploadSupportScreenshot(currentShopId, screenshotFile)
        } catch (error: any) {
          toast.warning('Screenshot upload failed', error?.message || 'Sending report without screenshot')
        }
      }

      await submitSupportReport({
        message: trimmedMessage,
        screenshotUrl,
        route,
        shopId: currentShopId ?? undefined,
        metadata: clientMetadata,
      })

      toast.success('Got it - we will take a look.')
      setOpen(false)
      resetForm()
    } catch (error: any) {
      toast.error('Failed to send report', error?.message || 'Please try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        className="fixed right-4 z-modal h-11 w-11 rounded-full border border-border bg-surface-primary/70 p-0 text-lg font-semibold text-content-secondary shadow-lg backdrop-blur-sm transition hover:bg-surface-primary/85 hover:text-content-primary bottom-[calc(var(--bottom-bar-height)+1rem+var(--safe-area-bottom))] md:right-6 md:bottom-6"
        aria-label="Report an issue"
        title="Report an issue"
      >
        <span aria-hidden>?</span>
      </Button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen)
          if (!nextOpen && !isSubmitting) {
            resetForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Something broken?</DialogTitle>
            <DialogDescription>
              Tell us what happened. We capture context automatically so we can move faster.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="support-message">What went wrong?</Label>
                <Textarea
                  id="support-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  error={messageError}
                  rows={5}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder="Describe what happened, what you expected, and what happened instead."
                />
                <p className="text-xs text-content-tertiary">
                  {message.length}/{MAX_MESSAGE_LENGTH}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Screenshot (optional)</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null
                    setScreenshotFile(file)
                  }}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImagePlus className="h-4 w-4" />
                    Upload screenshot
                  </Button>
                  {screenshotFile && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-tint/5 px-2 py-1 text-xs text-content-secondary">
                      {screenshotFile.name}
                      <button
                        type="button"
                        aria-label="Remove screenshot"
                        onClick={() => setScreenshotFile(null)}
                        className="text-content-tertiary hover:text-content-primary"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-tint/5 p-3 text-xs text-content-secondary">
                Context attached: route, browser, IP, account, and shop (if available).
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Send report
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
